import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, filter, lastValueFrom, map, of, tap } from 'rxjs';
import { environment } from '../environments/environment';
import {
    IUser,
    IUserSettings,
    Roles,
    IPaginatedQueryResult,
    BusinessServices,
    Country
} from '@shs-digi-care-monorepo/interfaces';
import { AuthService } from '../auth';
import { APP_BUSINESS_SERVICE } from './app-business-service.token';
import { CacheService } from './cache.service';

export const RuleEngineServiceName = 'RuleEngineService';
export const SystemServiceNames = [RuleEngineServiceName] as const;

/**
 * `GET /users/:gid` is served with a long `Cache-Control: max-age`, so an updated user would keep being replayed
 * from the browser cache. Varying the URL with a token that is generated once per application load makes every
 * reload fetch fresh data, while repeated lookups within the same session are served by {@link CacheService}.
 */
const CACHE_BUSTER_PARAM = 'v';
const cacheBuster = Date.now().toString(36);
const SETTINGS_CACHE_KEY = 'me-settings';

export interface BaseUserUpdatePayload {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    preferredLanguage?: string;
    country?: string;
    state?: string | null;
}

export type ICreateOrUpdateUserPayload = {
    gid: string;
    firstName: string;
    lastName: string;
    email: string;
    teams?: string[];
    /**
     * Roles to grant on creation. `PATCH /users/:gid` does not accept them - it rejects unknown
     * properties - so an existing user's roles are changed through {@link UserService.addUserRole}
     * and {@link UserService.removeUserRole} instead.
     */
    roleIds?: string[];
} & BaseUserUpdatePayload;

export type UserPaginated<T> = IPaginatedQueryResult & {
    data: T[];
};

export interface PatchUserSettings {
    stateTransitionNotificationsEnabled?: boolean;
    commentNotificationsEnabled?: boolean;
    reminderNotificationsEnabled?: boolean;
    emailHidingEnabled?: boolean;
    defaultPageSize?: number;
}

/**
 * Filters accepted by `GET /users`. That endpoint answers "which users do I administer in the
 * business service of this app": the countries within it are derived from the requestor's
 * administrative roles on the server, so role and country filters must not be sent.
 */
export interface BrowseUsersFilters {
    searchTerm?: string;
    teamId?: string;
    availableFrom?: string;
    availableTo?: string;
}

/**
 * Filters accepted by `GET /users/by-role`. That endpoint answers "who can be assigned in this
 * target context", so the scope of the target request, team or rule is supplied by the caller.
 * Without a stream the business service of this app is used.
 */
export interface UsersByRoleFilters extends BrowseUsersFilters {
    roles?: Roles[];
    stream?: BusinessServices;
    country?: Country;
}

interface PaginatedUsersResponse {
    items: IUser[];
    metadata: {
        itemCount: number;
        isZeroBased: boolean;
        take: number;
        currentPage: number;
        lastPage: number;
        hasNextPage: boolean;
    };
}

/**
 * Service for managing users via API.
 */
@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly httpClient = inject(HttpClient);
    private readonly authService = inject(AuthService);
    private readonly stream = inject(APP_BUSINESS_SERVICE);
    private readonly baseUrl = `${environment.apiUrl}`;
    private readonly cache = new CacheService<IUser>();
    private readonly settingsCache = new CacheService<IUserSettings>();

    /**
     * Fetches the current authenticated user.
     * @returns Observable of the current user
     */
    public getMe(): Observable<IUser | undefined> {
        return this.cache.getOrSet(
            'me',
            this.httpClient.get<IUser>(`${this.baseUrl}/me`).pipe(
                catchError((error) => {
                    if (error.message) {
                        console.error(error.message);
                    }
                    this.authService.logout();
                    throw error;
                })
            )
        );
    }

    /**
     * Invalidates the cached "me" user data.
     */
    public invalidateMeCache(): void {
        this.cache.invalidate('me');
    }

    /**
     * Updates the current authenticated user.
     * @param patch Partial user data to update
     * @returns Observable of the updated user
     */
    public patchMe(patch: Partial<BaseUserUpdatePayload>): Observable<IUser> {
        return this.httpClient.patch<IUser>(`${this.baseUrl}/me`, patch).pipe(
            tap((user) => {
                this.cache.set('me', user);
                this.cache.set(user.gid, user);
            })
        );
    }

    /**
     * Fetches a user by GID.
     * @param gid The user GID
     * @returns Observable of the user
     */
    public getByGID(gid: string): Observable<IUser> {
        if (gid === RuleEngineServiceName) {
            return of({} as IUser);
        }

        return this.cache.getOrSet(
            gid,
            this.fetchUser(gid).pipe(
                filter((user) => !!user),
                catchError((error) => {
                    if (error.message) {
                        console.error(error.message);
                    }
                    return of({} as IUser);
                })
            )
        );
    }

    /**
     * Fetches user names from API for multiple GIDs.
     * @param gids List of user GIDs for which to fetch usernames from the API
     * @returns Observable of array containing gid, firstName, lastName
     */
    public getUsernamesByGIDs(gids: string[]): Observable<Pick<IUser, 'gid' | 'firstName' | 'lastName'>[]> {
        if (gids.length === 0) {
            return of([]);
        }
        // Exclude system service names from API call
        const requestBody = {
            gids: gids.filter((gid) => !SystemServiceNames.includes(gid as (typeof SystemServiceNames)[number]))
        };
        return this.httpClient.post<IUser[]>(`${this.baseUrl}/users/get-usernames`, requestBody);
    }

    /**
     * Fetches a paginated list of the users the requestor administers in the business service of
     * this app.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @param filters Optional search, team and availability filters
     * @returns Observable of paginated users
     */
    public getUsers(
        page: number,
        pageSize: number,
        filters: BrowseUsersFilters = {}
    ): Observable<UserPaginated<IUser>> {
        const params = this.buildBaseParams(page, pageSize, filters).append('stream', this.stream);

        return this.fetchUsers('/users', page, pageSize, params);
    }

    /**
     * Fetches a paginated list of users that hold the given roles within the stream of the target
     * context, optionally narrowed down to its country. Used by the assignment pickers.
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @param filters Role scope of the target context plus the common search filters
     * @returns Observable of paginated users
     */
    public getUsersByRole(
        page: number,
        pageSize: number,
        filters: UsersByRoleFilters = {}
    ): Observable<UserPaginated<IUser>> {
        let params = this.buildBaseParams(page, pageSize, filters).append('stream', filters.stream ?? this.stream);

        if (filters.roles && filters.roles.length > 0) {
            params = params.append('roles', filters.roles.join(','));
        }
        if (filters.country) {
            params = params.append('country', filters.country);
        }

        return this.fetchUsers('/users/by-role', page, pageSize, params);
    }

    private buildBaseParams(page: number, pageSize: number, filters: BrowseUsersFilters): HttpParams {
        let params = new HttpParams().append('page', page).append('pageSize', pageSize);

        if (filters.searchTerm) {
            params = params.append('searchTerm', filters.searchTerm);
        }
        if (filters.teamId) {
            params = params.append('teamId', filters.teamId);
        }
        if (filters.availableFrom) {
            params = params.append('availableFrom', filters.availableFrom);
        }
        if (filters.availableTo) {
            params = params.append('availableTo', filters.availableTo);
        }

        return params;
    }

    private fetchUsers(
        path: string,
        page: number,
        pageSize: number,
        params: HttpParams
    ): Observable<UserPaginated<IUser>> {
        return this.httpClient.get<PaginatedUsersResponse>(`${this.baseUrl}${path}`, { params }).pipe(
            map((response) => ({
                data: response.items || [],
                page: response.metadata?.currentPage ?? page,
                pageSize: response.metadata?.take ?? pageSize,
                total: response.metadata?.itemCount ?? 0
            }))
        );
    }

    /**
     * Fetches a user by their GID.
     * @param gid The user GID
     * @returns Observable of the user
     */
    public getUserByGid(gid: string): Observable<IUser> {
        return this.cache.getOrSet(gid, this.fetchUser(gid));
    }

    /**
     * Fetch user by email
     * @param email The email of the user
     * @returns Observable of the user
     */
    public getUserByEmail(email: string): Observable<IUser> {
        return this.getUsers(0, 1, { searchTerm: email }).pipe(
            map((result) => {
                const users = result.data || [];
                return users?.[0] ?? undefined;
            })
        );
    }

    /**
     * Creates a new user.
     * @param user User data
     * @returns Promise of the created user
     */
    public async createUser(user: ICreateOrUpdateUserPayload): Promise<IUser> {
        return await lastValueFrom(this.httpClient.post<IUser>(`${this.baseUrl}/users`, user));
    }

    /**
     * Updates the profile of an existing user. Role assignments are not part of the patch payload;
     * the API rejects them and serves them through dedicated endpoints instead.
     * @param user User data with gid
     * @returns Observable of the updated user
     */
    public updateUser(user: ICreateOrUpdateUserPayload): Observable<IUser> {
        const { gid, roleIds: _roleIds, ...userBody } = user;
        return this.httpClient
            .patch<IUser>(`${this.baseUrl}/users/${gid}`, userBody)
            .pipe(tap((updated) => this.cache.set(gid, updated)));
    }

    /**
     * Grants a single role to a user.
     * @param gid The user GID
     * @param roleId Id of the role from the role catalogue
     * @returns Observable of the updated user
     */
    public addUserRole(gid: string, roleId: string): Observable<IUser> {
        return this.httpClient
            .put<IUser>(`${this.baseUrl}/users/${gid}/roles/${roleId}`, null)
            .pipe(tap((updated) => this.cache.set(gid, updated)));
    }

    /**
     * Revokes a single role from a user. The API rejects the removal of the last team-eligible role
     * while the user still holds teams, so clear the teams first.
     * @param gid The user GID
     * @param roleId Id of the role from the role catalogue
     * @returns Observable of the updated user
     */
    public removeUserRole(gid: string, roleId: string): Observable<IUser> {
        return this.httpClient
            .delete<IUser>(`${this.baseUrl}/users/${gid}/roles/${roleId}`)
            .pipe(tap((updated) => this.cache.set(gid, updated)));
    }

    /**
     * Atomically swaps one of a user's role assignments for another. Use this instead of a separate
     * remove + add when neither ordering is valid on its own - e.g. moving a requestor to another
     * country, where the user holds Requestor as their only role.
     * @param gid The user GID
     * @param removeRoleId Id of the role assignment to remove
     * @param addRoleId Id of the role to grant in its place
     * @returns Observable of the updated user
     */
    public replaceUserRole(gid: string, removeRoleId: string, addRoleId: string): Observable<IUser> {
        return this.httpClient
            .post<IUser>(`${this.baseUrl}/users/${gid}/roles/replace`, { removeRoleId, addRoleId })
            .pipe(tap((updated) => this.cache.set(gid, updated)));
    }

    /**
     * Replaces the team assignments of a user.
     * @param gid The user GID
     * @param teams The team IDs the user should belong to
     * @returns Observable of the updated user
     */
    public updateUserTeams(gid: string, teams: string[]): Observable<IUser> {
        return this.httpClient
            .patch<IUser>(`${this.baseUrl}/users/${gid}`, { teams })
            .pipe(tap((updated) => this.cache.set(gid, updated)));
    }

    /**
     * Deletes a user by their GID.
     * @param gid The user GID
     * @returns Observable of void
     */
    public deleteUser(gid: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/users/${gid}`).pipe(tap(() => this.cache.invalidate(gid)));
    }

    private fetchUser(gid: string): Observable<IUser> {
        const params = new HttpParams().set(CACHE_BUSTER_PARAM, cacheBuster);

        return this.httpClient.get<IUser>(`${this.baseUrl}/users/${gid}`, { params }).pipe(
            catchError((error) => {
                this.cache.invalidate(gid);
                throw error;
            })
        );
    }
    /**
     * Fetches user settings for the current user. The result is cached for the lifetime of the application and kept
     * up to date by {@link updateUserSettings}.
     * @returns Observable of user settings
     */
    public getUserSettings(): Observable<IUserSettings> {
        return this.settingsCache.getOrSet(
            SETTINGS_CACHE_KEY,
            this.httpClient.get<IUserSettings>(`${this.baseUrl}/me/settings`).pipe(
                catchError((error) => {
                    this.settingsCache.invalidate(SETTINGS_CACHE_KEY);
                    throw error;
                })
            )
        );
    }

    /**
     * Updates user settings for the current user.
     * @param settings Partial user settings to update
     * @returns Observable of the updated user settings
     */
    public updateUserSettings(settings: PatchUserSettings): Observable<IUserSettings> {
        return this.httpClient
            .patch<IUserSettings>(`${this.baseUrl}/me/settings`, settings)
            .pipe(tap((updated) => this.settingsCache.set(SETTINGS_CACHE_KEY, updated)));
    }
}
